import {Component, signal, inject, DestroyRef} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {EmployeeRank} from '../../../../classes/models/employee/EmployeeRank';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {EmployeeRankService} from '../../../../services/http/employee/employee-rank.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Data required to open the employee rank delete modal. */
export interface EmployeeRankDeleteData {
  employeeRank: EmployeeRank;
  employeePositionId?: EmployeePositionId;
}

/**
 * This component handles deleting employee's rank.
 */
@Component({
  selector: 'app-employee-rank-delete',
  templateUrl: './employee-rank-delete.component.html',
  styleUrls: ['./employee-rank-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class EmployeeRankDeleteComponent {
  private employeeRankService = inject(EmployeeRankService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId>(undefined);
  /** An employee rank to delete. */
  readonly employeeRank = signal<EmployeeRank>(undefined);

  /** `True` if request sending to backend is in progress. */
  loading = signal(false);

  static open(modalService: NgbModal, data: EmployeeRankDeleteData): NgbModalRef {
    const modalRef = modalService.open(EmployeeRankDeleteComponent);
    const instance = modalRef.componentInstance as EmployeeRankDeleteComponent;
    instance.employeeRank.set(data.employeeRank);
    if (data.employeePositionId !== undefined) {
      instance.employeePositionId.set(data.employeePositionId);
    }
    return modalRef;
  }

  /**
   * Sends employee rank deletion request to backend.
   * @param modalComponent a modal component.
   */
  confirm(modalComponent: ModalComponent): void {
    this.loading.set(true);

    this.employeeRankService.deleteEmployeeRank(this.employeePositionId(), this.employeeRank().startDate)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading.set(false);
                this.toastr.success('Employee rank successfully deleted');
                this.ruleCheckerService.check();
              },
              error: error => {
                this.loading.set(false);
                this.toastr.error(error.error.message);
              }
            });
  }
}